import React, { useEffect } from 'react';
import axios from 'axios';
import { Container, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { captureException, addBreadcrumb } from '@sentry/react';
import { useMessage } from 'modules/intl';
import { useUpdateSettingPreferencesForCollectorSetting } from '../hooks/useUpdateSettingPreferencesForCollectorSetting';
import { CollectorIntermediatePageSkeleton } from './CollectorIntermediatePageSkeleton';
import { InstallBanner } from './InstallBanner';
import {
  useInstallCollector,
  collectorBroadcast,
  useCollectorItem
} from './hooks';
import useCollectorIntermediateStyles from './hooks/useCollectorIntermediateStyles';

const refreshDesktopCollectors = async (refreshURL: string) => {
  try {
    await axios.get(refreshURL);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { ecid } = error.response.headers;
      addBreadcrumb({
        type: 'http',
        category: 'refreshDesktopCollectors',
        data: { ecid: ecid, status: error.response.status },
        level: 'info'
      });
    }
    captureException(error);
  }
};

export const CollectorSuccess: React.FC = () => {
  const classes = useCollectorIntermediateStyles();
  const {
    collectorId,
    collectorItem,
    scope,
    loading,
    syncRequired,
    noAction,
    collectorMessage
  } = useCollectorItem();
  const { installCollector } = useInstallCollector();
  const refreshURL = localStorage.getItem('collectorDesktopRefreshURL');
  const {
    updateSettingPreferences
  } = useUpdateSettingPreferencesForCollectorSetting();

  const [installError, setInstallError] = React.useState<string | undefined>(
    undefined
  );
  const [installUpdated, setInstallUpdated] = React.useState<boolean>(false);
  const installFailReason = useMessage('collectors.installFailReason');
  useEffect(() => {
    const saveStatus = async () => {
      if (collectorId && !noAction) {
        try {
          const dataSet = await installCollector(collectorId, scope);
          if (syncRequired) {
            await updateSettingPreferences({
              installId: dataSet?.data?.installCollector?.installId,
              settings: [
                {
                  key: 'syncStatus',
                  value: 'InProgress'
                }
              ]
            });
          }
          setInstallUpdated(true);
        } catch (e) {
          setInstallUpdated(true);
          const msg = (e as Error).message;
          if (msg === 'Collector is already installed') {
            console.info(msg);
          } else {
            setInstallError(msg ?? installFailReason);
            console.error(e);
          }
        }
        if (refreshURL) {
          await refreshDesktopCollectors(refreshURL);
          localStorage.removeItem('collectorDesktopRefreshURL');
        } else {
          await collectorBroadcast.postMessage(collectorId);
        }
      } else {
        setInstallUpdated(true);
      }
    };
    saveStatus();
  }, [
    collectorId,
    installCollector,
    scope,
    refreshURL,
    syncRequired,
    updateSettingPreferences,
    noAction,
    installFailReason
  ]);

  const message = useMessage('collectors.installSuccessMessage2', {
    collector: collectorItem?.name
  });

  if (loading || !installUpdated) return <CollectorIntermediatePageSkeleton />;
  return (
    <div
      className={classes.root}
      data-testid={
        installError ? 'collector-fail-page' : 'collector-success-page'
      }
      role="alert"
      aria-label={message}
    >
      <Container className={classes.textContainer}>
        {installError ? (
          <Typography variant="h2" data-testid="install-fail-message-1">
            <FormattedMessage id="collectors.installFailMessage1" />
          </Typography>
        ) : (
          <Typography variant="h2">
            <FormattedMessage id="collectors.installSuccessMessage1" />
          </Typography>
        )}
      </Container>
      <InstallBanner imageSrc={collectorItem?.imageSrc} classes={classes} />
      <Container className={classes.textContainer}>
        {syncRequired && !installError && (
          <div data-testid="collector-syncMessage">
            <Typography align="center" variant="body1">
              <FormattedMessage
                id={`collectors.syncMessages.${collectorId}`}
                values={{ collector: collectorItem?.name }}
              />
            </Typography>
          </div>
        )}

        {installError ? (
          <Typography variant="h4">
            <FormattedMessage
              id="collectors.installFailMessage2"
              values={{ collector: collectorItem?.name }}
            />
          </Typography>
        ) : (
          <Typography variant="h4">
            <FormattedMessage
              id="collectors.installSuccessMessage2"
              values={{ collector: collectorItem?.name }}
              data-testid="install-fail-message-2"
            />
          </Typography>
        )}
        {installError && (
          <Typography data-testid="collector-install-error" variant="h4">
            {installError}
          </Typography>
        )}
        {!installError && collectorMessage && (
          <Typography data-testid="collector-message" variant="h4">
            {collectorMessage}
          </Typography>
        )}

        {installError ? (
          <Typography variant="h4">
            <FormattedMessage
              id="collectors.installFailMessage3"
              data-testid="install-fail-message-3"
            />
          </Typography>
        ) : (
          <Typography variant="h4">
            <FormattedMessage id="collectors.installSuccessMessage3" />
          </Typography>
        )}
      </Container>
    </div>
  );
};

export default React.memo(CollectorSuccess);
